import { Input } from "@shared-ui/input";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState } from "react";
import { searchAdminCategories } from "@/features/catalog/services/catalog-api.service";
import { useDebounce } from "@/lib/use-debounce";

/**
 * A searchable category picker.
 *
 * A `<select>` was the obvious thing and it is the wrong thing: the taxonomy has
 * 674 leaf categories, and a 674-row dropdown is not a control anyone can use.
 * So it searches the server instead of scrolling a list.
 *
 * (An earlier version of this comment also claimed the endpoint caps a page at
 * 100 and would silently truncate. It does not -- the admin categories endpoint
 * takes no page parameters at all. The control is still right; that particular
 * reason for it was invented.)
 *
 * The two consumers that justify it as a component rather than page code: this
 * editor's category field, and the product list's category filter.
 */
export function CategoryPicker({
	id,
	label,
	value,
	valueLabel,
	onChange,
	disabled,
	clearable = true,
}: {
	/** Id for the input, so a <Label htmlFor> outside can address it. */
	id?: string;
	/**
	 * An accessible name, where the caller has an `id` but NO visible label.
	 *
	 * The `aria-label` below assumed an `id` implied a `<Label htmlFor>`, which
	 * held while there was one picker on a screen. The product list now shows two
	 * -- a category FILTER and the bulk bar's new category VALUE -- and both sat
	 * in bars whose other controls are named by `aria-label` rather than by a
	 * visible label, so both announced nothing and were indistinguishable to a
	 * screen reader and to a test.
	 */
	label?: string;
	/** Currently selected category id, or null. */
	value: string | null;
	/** Its name, so the field can show a selection it did not search for. */
	valueLabel: string | null;
	onChange: (category: { id: string; name: string } | null) => void;
	disabled?: boolean;
	/**
	 * Whether the operator may clear the selection. False where the field is
	 * required and an empty value has no meaning -- a product must have a
	 * category, so offering Clear there only produces a 400 on save.
	 */
	clearable?: boolean;
}) {
	const [query, setQuery] = useState("");
	const [open, setOpen] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const generatedId = useId();
	const listId = `${id ?? generatedId}-listbox`;
	/*
	 * Which option the arrow keys are on. -1 means "none", which is the state the
	 * list opens in -- pre-selecting the first result would make Enter commit a
	 * category the operator never looked at.
	 */
	const [activeIndex, setActiveIndex] = useState(-1);

	const debounced = useDebounce(query, 250);

	/*
	 * A TERM IS REQUIRED, because an empty one is not a cheap query.
	 *
	 * `searchAdminCategories("")` drops the empty `q` and the endpoint takes no
	 * page parameters at all, so it returns the entire taxonomy -- 674 rows, all
	 * eighteen columns, plus an unfiltered product-count aggregate over
	 * `catalog_products` -- and it did that on every focus of a field the editor
	 * always shows. The picker is a search control; making it search is the fix.
	 */
	const term = debounced.trim();
	const results = useQuery({
		queryKey: ["admin-catalog-category-search", term],
		queryFn: () => searchAdminCategories(term),
		enabled: open && term.length > 0,
	});

	/*
	 * Close on an outside click OR an outside focus.
	 *
	 * `mousedown` alone covers the mouse and misses the keyboard entirely: an
	 * operator who tabs out of the field left the absolutely-positioned list
	 * hanging over the fields below it, with no way to dismiss it but to come
	 * back and click. `focusin` is the keyboard's equivalent of the same event,
	 * and both are scoped through `containerRef` so a click or a tab onto a
	 * RESULT does not close the list before the selection lands.
	 */
	useEffect(() => {
		if (!open) return;
		const closeIfOutside = (event: Event) => {
			if (!containerRef.current?.contains(event.target as Node)) {
				setOpen(false);
			}
		};
		document.addEventListener("mousedown", closeIfOutside);
		document.addEventListener("focusin", closeIfOutside);
		return () => {
			document.removeEventListener("mousedown", closeIfOutside);
			document.removeEventListener("focusin", closeIfOutside);
		};
	}, [open]);

	const items = results.data ?? [];
	const activeId =
		activeIndex >= 0 ? `${listId}-option-${activeIndex}` : undefined;

	function commit(category: { id: string; name: string }): void {
		onChange({ id: category.id, name: category.name });
		setOpen(false);
		setQuery("");
		setActiveIndex(-1);
	}

	return (
		<div ref={containerRef} className="relative">
			<Input
				id={id}
				placeholder={valueLabel ?? "Search categories"}
				value={open ? query : (valueLabel ?? "")}
				disabled={disabled}
				role="combobox"
				aria-expanded={open}
				aria-controls={listId}
				aria-autocomplete="list"
				aria-activedescendant={activeId}
				// An explicit `label` wins. Otherwise: named "Category" when there is
				// no id, and left to a <Label htmlFor> when there is one -- where a
				// second name would both duplicate and override it.
				aria-label={label ?? (id ? undefined : "Category")}
				onFocus={() => {
					setOpen(true);
					setQuery("");
					setActiveIndex(-1);
				}}
				// Escape closes without choosing. Without it the only way out of an
				// open list is a click elsewhere, which on a form means clicking into
				// a field the operator did not mean to edit.
				onKeyDown={(event) => {
					if (event.key === "Escape" && open) {
						event.stopPropagation();
						setOpen(false);
						setQuery("");
						setActiveIndex(-1);
						return;
					}
					if (!open || items.length === 0) return;
					if (event.key === "ArrowDown") {
						event.preventDefault();
						setActiveIndex((index) => (index + 1) % items.length);
						return;
					}
					if (event.key === "ArrowUp") {
						event.preventDefault();
						setActiveIndex((index) => (index <= 0 ? items.length : index) - 1);
						return;
					}
					if (event.key === "Enter" && activeIndex >= 0) {
						event.preventDefault();
						commit(items[activeIndex]);
					}
				}}
				onChange={(event) => {
					setQuery(event.target.value);
					// The highlighted row is meaningless once the result set changes
					// under it -- keeping the index would move the highlight onto a
					// different category than the one the operator was looking at.
					setActiveIndex(-1);
				}}
			/>

			{clearable && value && !open ? (
				<button
					type="button"
					aria-label="Clear category"
					className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground hover:text-foreground"
					onClick={() => onChange(null)}
				>
					Clear
				</button>
			) : null}

			{open ? (
				<div
					id={listId}
					// `listbox` on the popup and `option` on each row: the input already
					// claims `role="combobox"`, and a combobox pointing `aria-controls`
					// at a plain div promises a list that assistive tech cannot find.
					role="listbox"
					aria-label="Category results"
					className="absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-md border bg-white shadow-md"
				>
					{term.length === 0 ? (
						<p className="p-3 text-sm text-muted-foreground">
							Type to search categories.
						</p>
					) : results.isError ? (
						/*
						 * A FAILED search is not an empty one, and this branch exists
						 * because rendering both as "No categories match" told an operator
						 * the taxonomy had no Tiles category when the request had simply
						 * failed. The field is required and cannot be cleared, so that
						 * message is a dead end rather than an inconvenience.
						 */
						<div className="p-3 text-sm text-red-700">
							Could not search categories.{" "}
							<button
								type="button"
								className="underline"
								onClick={() => results.refetch()}
							>
								Try again
							</button>
						</div>
					) : results.isPending || results.isFetching ? (
						<p className="p-3 text-sm text-muted-foreground">Searching…</p>
					) : items.length === 0 ? (
						<p className="p-3 text-sm text-muted-foreground">
							No categories match “{term}”.
						</p>
					) : (
						<ul>
							{items.map((category, index) => (
								<li key={category.id}>
									<button
										type="button"
										id={`${listId}-option-${index}`}
										role="option"
										aria-selected={index === activeIndex}
										className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-100 focus:bg-slate-100 focus:outline-none${
											index === activeIndex ? " bg-slate-100" : ""
										}`}
										onMouseEnter={() => setActiveIndex(index)}
										onClick={() => commit(category)}
									>
										{category.name}
									</button>
								</li>
							))}
						</ul>
					)}
				</div>
			) : null}
		</div>
	);
}
